import { useCallback } from 'react';
import { useAgentStore } from '@/stores/agentStore';
import { useNodeStore } from '@/stores/nodeStore';
import { useTaskStore } from '@/stores/taskStore';
import { AgentService } from '@/services/agent';
import { NodeType, NodeStatus } from '@/types';
import type { MediaGenerateResponse } from '@/types/agent';
import { generateApi } from '@/services/generateApi';
import { saveAssets } from '@/utils/assetUtils';
import { createLocalId, extractAllContentFromText, finalizeProcessLogs, formatFinalAssistantText, stripThinkingAndToolCallTags } from '../helpers';
import { Message } from '../types';
import { useAgentMessages } from './useAgentMessages';
import { useAgentCanvasOps } from './useAgentCanvasOps';
// 🔵 使用统一的 ProcessLogs 管理器
import {
  markThinkingLogComplete,
  addProcessLogsFromTextChunk,
  addActiveProcessLog,
  getActiveProcessLogs,
} from '../processLogManager';

/**
 * 为智能体异步任务启动轮询回退。
 * 当 WebSocket 不可用或未及时推送结果时，通过 HTTP 轮询获取任务状态。
 */
function startAgentTaskPolling(
  taskId: string,
  nodeId: string,
  nodeType: string,
  isVideo: boolean,
) {
  const MAX_POLL_ATTEMPTS = 360; // 最多轮询 360 次
  const POLL_INTERVAL = 5000; // 每 5 秒一次
  let attempt = 0;

  const poll = async () => {
    while (attempt < MAX_POLL_ATTEMPTS) {
      attempt++;
      try {
        const statusResult = await generateApi.getTaskStatus(taskId);

        if (statusResult.status === 'success' && statusResult.result) {
          // 任务成功完成，更新画布节点
          const result = statusResult.result;
          const results: Array<{ url: string; outputType?: string }> = result.results || [];
          const urls = results.map((r: any) => r.url).filter(Boolean) as string[];

          const node = useNodeStore.getState().nodeMap.get(nodeId);
          if (node && node.status === NodeStatus.WORKING && urls.length > 0) {
            const projectId = useAgentStore.getState().projectId;
            let savedUrls = urls;
            if (projectId) {
              try {
                const { savedUrls: su } = await saveAssets(projectId, urls, {
                  nodeId,
                  prefix: `agent_${isVideo ? 'video' : 'image'}`,
                  metadata: {
                    generator_node_id: nodeId,
                    generator_type: isVideo ? 'agent_video' : 'agent_image',
                  },
                  isVideo,
                });
                savedUrls = su;
              } catch (err) {
                console.error('[AgentTaskPoll] 保存资产失败:', err);
              }
            }

            const nodeUpdates: Record<string, any> = { progress: undefined };
            if (isVideo) {
              nodeUpdates.videoUri = savedUrls[0] || urls[0];
              nodeUpdates.videoUris = savedUrls.length > 0 ? savedUrls : urls;
              if (result.videoMetadata) {
                nodeUpdates.videoMetadata = result.videoMetadata;
                nodeUpdates.duration = typeof result.videoMetadata.duration === 'number' ? result.videoMetadata.duration : undefined;
              }
            } else {
              nodeUpdates.image = savedUrls[0] || urls[0];
              nodeUpdates.images = savedUrls.length > 0 ? savedUrls : urls;
            }

            useNodeStore.getState().updateNode(nodeId, nodeUpdates, undefined, undefined, NodeStatus.SUCCESS);
            console.log(`[AgentTaskPoll] Task ${taskId} completed, updated node ${nodeId}`);
          }

          // 同步更新智能体面板消息
          useAgentStore.getState().setMessages((prev) =>
            prev.map((m) => {
              const matchByMediaData = m.mediaData && m.mediaData.taskId === taskId;
              const matchByMetadata = m.metadata?.pendingTaskId === taskId;
              if (matchByMediaData || matchByMetadata) {
                return {
                  ...m,
                  mediaData: m.mediaData
                    ? {
                        ...m.mediaData,
                        status: 'SUCCESS',
                        results: result.results || m.mediaData?.results || [],
                        ...(result.videoMetadata ? { videoMetadata: result.videoMetadata } : {}),
                      }
                    : undefined,
                  text: '',
                  metadata: {
                    ...m.metadata,
                    taskStatus: 'COMPLETED',
                  },
                };
              }
              return m;
            })
          );
          console.log(`[AgentTaskPoll] Updated agent panel message for task ${taskId}`);

          // 更新 taskStore
          useTaskStore.getState().updateTaskStatus(taskId, 'SUCCESS');
          useTaskStore.getState().removeTask(taskId);
          return; // 轮询结束
        } else if (statusResult.status === 'failed') {
          // 任务失败
          useNodeStore.getState().updateNode(nodeId, { error: statusResult.error || '任务失败', progress: undefined }, undefined, undefined, NodeStatus.ERROR);

          // 同步更新智能体面板消息
          useAgentStore.getState().setMessages((prev) =>
            prev.map((m) => {
              const matchByMediaData = m.mediaData && m.mediaData.taskId === taskId;
              const matchByMetadata = m.metadata?.pendingTaskId === taskId;
              if (matchByMediaData || matchByMetadata) {
                return {
                  ...m,
                  mediaData: m.mediaData
                    ? {
                        ...m.mediaData,
                        status: 'FAILED',
                        error: statusResult.error || '任务失败',
                      }
                    : undefined,
                  text: `生成失败：${statusResult.error || '未知错误'}`,
                  metadata: {
                    ...m.metadata,
                    taskStatus: 'FAILED',
                  },
                };
              }
              return m;
            })
          );

          useTaskStore.getState().updateTaskStatus(taskId, 'FAILED');
          useTaskStore.getState().removeTask(taskId);
          console.warn(`[AgentTaskPoll] Task ${taskId} failed: ${statusResult.error}`);
          return; // 轮询结束
        }

        // 任务仍在运行，继续轮询
        await new Promise(r => setTimeout(r, POLL_INTERVAL));
      } catch (err) {
        console.error(`[AgentTaskPoll] Error polling task ${taskId}:`, err);
        await new Promise(r => setTimeout(r, POLL_INTERVAL));
      }
    }

    // 超时
    console.warn(`[AgentTaskPoll] Task ${taskId} polling timed out after ${MAX_POLL_ATTEMPTS} attempts`);
    useNodeStore.getState().updateNode(nodeId, { error: '任务超时', progress: undefined }, undefined, undefined, NodeStatus.ERROR);
    useTaskStore.getState().removeTask(taskId);
  };

  // 延迟 15 秒后开始轮询，给 WebSocket 一个先推送结果的机会
  setTimeout(poll, 15000);
}

async function handleCollaborationCanvasResults(
  collaborationResults: Array<{ content: string; resultType?: string; taskId?: string }>,
  deps: {
    ensureCanvasGenerationNode: (params: any) => string;
    syncCanvasGenerationNodeResult: (nodeId: string, mediaData: MediaGenerateResponse) => Promise<void>;
    updateCanvasNode: (id: string, data: any) => void;
    userText: string;
    currentModelId: string;
    currentRefs: any[];
    currentAttachments: any[];
  },
) {
  for (const cr of collaborationResults) {
    try {
      const parsed = JSON.parse(cr.content);
      const taskType = cr.resultType || parsed.taskType;
      const taskId = parsed.taskId;
      if ((taskType === 'image' || taskType === 'video' || taskType === 'grid') && taskId) {
        const isImage = taskType === 'image' || taskType === 'grid';
        const canvasNodeId = deps.ensureCanvasGenerationNode({
          taskType: isImage ? 'image' : 'video',
          prompt: parsed.prompt || deps.userText,
          modelId: parsed.modelId || deps.currentModelId,
          currentRefs: deps.currentRefs,
          hasAttachmentInput: deps.currentAttachments.length > 0,
          generationParams: undefined,
        });
        const mediaData = parsed as MediaGenerateResponse;
        await deps.syncCanvasGenerationNodeResult(canvasNodeId, mediaData);
        const isPending = mediaData.status === 'PENDING' || mediaData.status === 'RUNNING' || mediaData.status === 'QUEUED';
        if (isPending && mediaData.taskId) {
          const nodeType = isImage ? NodeType.IMAGE_GENERATOR : NodeType.VIDEO_GENERATOR;
          useTaskStore.getState().addTaskWithRequest(mediaData.taskId, canvasNodeId, nodeType, {
            modelId: mediaData.modelId || deps.currentModelId,
            prompt: mediaData.prompt || deps.userText,
            params: { aspectRatio: mediaData.aspectRatio || '16:9' },
          });
          deps.updateCanvasNode(canvasNodeId, { taskId: mediaData.taskId });
          // 启动轮询回退，确保即使 WebSocket 不可用也能获取结果
          startAgentTaskPolling(mediaData.taskId, canvasNodeId, nodeType, !isImage);
        }
      }
    } catch (err) {
      // 🔴 v5-#11: 不再静默吞错，添加错误日志
      // 🔴 v6-#5: 添加用户可见的处理记录提示
      console.error('[CollabCanvas] Failed to process collaboration result:', err);
      addActiveProcessLog('error', '⚠️ 协作结果处理失败', String(err), 'error');
    }
  }
}

export function useStreamCompletionHandler() {
  const { finalizeAssistantMessage } = useAgentMessages();
  const { ensureCanvasGenerationNode, syncCanvasGenerationNodeResult } = useAgentCanvasOps();
  const updateCanvasNode = useNodeStore((s) => s.updateNode);

  // 🔵 P2: 使用 ActiveReplyBuilder 的统一完成处理
  // 核心逻辑：所有消息类型（文本/搜索/生成确认）都从 builder 生成，消除双路竞态
  // 🔵 P1-5: 标志位已移除，统一通过 store.activeReplyBuilder.type 判断场景
  const handleComplete = useCallback(async (state: {
    accumulatedText: string;
    accumulatedThinking: string;
    currentIntent: { name: string; confidence: number; source: string } | null;
    userText: string;
    currentAttachments: any[];
    currentRefs: any[];
    currentModelId: string;
    streamSessionId: string;
    refreshChatHistory: () => void;
  }) => {
    const store = useAgentStore.getState();
    // 🔵 标记流式会话已结束，防止后续回调重新初始化 builder
    useAgentStore.setState({ streamEnded: true });
    // 🔵 #19: clearThinkingTimer 已包含在 resetStreamingState 中，此处不再单独调用
    store.setIsThinking(false);

    // 获取 builder（在流式过程中由各回调更新）
    const builder = store.activeReplyBuilder;
    // 当前累积的处理日志
    const currentLogs = store.activeProcessLogs;

    // 🔵 P2: 根据 builder.type 决定最终消息形态
    // 关键：无论哪种类型，都只生成一条消息；统一通过 finalizeAssistantMessage 完成"构造+推送+同步"
    if (builder) {
      const msgType = builder.type || 'text';
      const msgId = builder.id || createLocalId();

      if (msgType === 'generation_confirm') {
        // === 生成确认消息：复用 finalizeAssistantMessage，消除重复的后端同步代码 ===
        // 🔵 v3-#4: 传入 skipBackendId: true，因为 msgId 已从 builder.id 获取，避免二次解析
        await finalizeAssistantMessage({
          id: msgId,
          type: 'generation_confirm',
          text: builder.text || '请确认生成参数',
          generationOptions: builder.generationOptions,
          pendingUserText: (builder.metadata || {}).pendingUserText,
          pendingAttachments: (builder.metadata || {}).pendingAttachments,
          pendingReferences: (builder.metadata || {}).pendingReferences,
          pendingRefNodeIds: (builder.metadata || {}).pendingRefNodeIds,
          introText: builder.introText || undefined,
          skipBackendId: true,
        } as any);
      } else if (msgType === 'search') {
        // === 搜索消息：复用 finalizeAssistantMessage ===
        // 将 warning 写入 searchData，以便 buildSearchListProps 可以提取
        const searchDataWithWarning = builder.searchData
          ? { ...builder.searchData, warning: (builder as any).warning || undefined }
          : builder.searchData;
        // P6-2: 在保存时直接剥离 LLM 标签，避免历史消息恢复时重复解析
        // renderFormattedMessage 内部也会调用 stripThinkingAndToolCallTags，
        // 但提前做一次可减少双重处理的边界情况（如历史消息恢复时 builder.text 已丢失）
        // I2: 先取原始文本再用 || 做兜底，正确处理空字符串（''为falsy，但trim后仍为''才应fallback）
        // P0(v5): 合并 pendingMarkdown（被 chunk 分割的 markdown 尾部暂存）到最终文本
        // 🔴 NEW-3: 清理残留的完整标签 [label]（模式 3 缓冲后流意外结束）
        // 场景：[京东] 被缓冲等待下一个 chunk 的 (URL)，但流意外断开
        // 如果不清理，用户会看到 [京东] 而非 京东
        let pendingMd = (builder as any).pendingMarkdown || '';
        // 模式3: [label] → label（被缓冲等待 URL 的独立标签）
        pendingMd = pendingMd.replace(/^\[([^[\]]+)\]$/, '$1');
        // 模式1: [label](url → label（链接标签完整但 URL 缺少闭合 )）
        // 场景：流式输出到 "[京东](https://item.jd.com/1234" 时流异常中断
        pendingMd = pendingMd.replace(/^\[([^\]]+)\]\([^)]*$/, '$1');
        // 模式2: [label → label（标签缺少闭合 ]）
        // 场景：流式输出到 "[京东" 时流异常中断
        pendingMd = pendingMd.replace(/^\[([^[\]]+)$/, '$1');
        const rawText = (builder.text || '') + pendingMd;
        // P0(v8): 对最终文本做标签清理，防止流式阶段泄漏的 <think...> 等标签写入历史消息
        // 流式逐 chunk 清理时，跨 chunk 的标签可能未被完全清除
        const cleanText = stripThinkingAndToolCallTags(rawText).trim() || '搜索结果';
        // P0修复：将 builder.text（AI摘要流式文本）写入 searchData.summary
        // SearchResultsList 使用 displayContent = streamingSummary || answer || summary
        // 历史消息阶段 streamingSummary=undefined，所以 summary 必须有内容
        // P1(v10): 始终使用 cleanText 作为 summary，避免初始摘要与完整摘要重叠导致重复
        const searchDataWithSummary = searchDataWithWarning
          ? {
              ...searchDataWithWarning,
              summary: cleanText,
            }
          : searchDataWithWarning;
        // A1: introText 是简短描述（如"搜索「格力净水机」的结果"），
        // 始终保留，不与 cleanText 比较去重（P0(v9) 后 introText 不再是完整摘要）
        const effectiveIntroText = builder.introText || undefined;
        await finalizeAssistantMessage({
          id: msgId,
          type: 'search',
          text: cleanText,
          searchData: searchDataWithSummary,
          introText: effectiveIntroText,
          processLogs: getActiveProcessLogs(),
          skipBackendId: true,
        } as any);
      } else {
        // === 文本消息（默认）：使用 finalizeAssistantMessage ===
        // 把思考标记为完成 + 文本清理 + 协作日志处理 + 最终化
        if (state.accumulatedThinking) {
          markThinkingLogComplete();
        }
        const rawText = state.accumulatedText || builder.text || '';
        const extracted = extractAllContentFromText(rawText);
        const cleanText = extracted.cleanText || '已完成回复生成';

        // 警告日志（生图调用但未确认）
        // P1-5: 用 builder.generationOptions 替代标志位，若 builder 中没有 generationOptions
        // 但文本中有调用标签 → 视为未收到确认
        if (!builder.generationOptions && rawText.includes('CALL_AGENT:image-artist')) {
          const logs = getActiveProcessLogs();
          const exists = logs.some(l => l.title.includes('生图调用可能未完成'));
          if (!exists) {
            addActiveProcessLog('action', '⚠️ 生图调用可能未完成', '检测到协作标签但未收到确认响应', 'completed');
          }
        }

        // 协作画布节点生成
        if (extracted.collaborationResults.length > 0) {
          await handleCollaborationCanvasResults(extracted.collaborationResults, {
            ensureCanvasGenerationNode,
            syncCanvasGenerationNodeResult,
            updateCanvasNode,
            userText: state.userText,
            currentModelId: state.currentModelId,
            currentRefs: state.currentRefs,
            currentAttachments: state.currentAttachments,
          });
        }

        // 文本中提取的动态日志
        if (extracted.processLogs.length > 0) {
          addProcessLogsFromTextChunk(rawText);
        }

        await finalizeAssistantMessage({
          id: msgId,
          text: formatFinalAssistantText({
            text: cleanText,
            intentName: state.currentIntent?.name,
            userText: state.userText,
            hasVisualContext: state.currentAttachments.length > 0 || state.currentRefs.length > 0,
          }),
          type: 'text',
        });
      }
    } else {
      // 🔵 v3-#2: 备用路径简化 — 直接走 finalizeAssistantMessage，不再手动调用 markThinkingLogComplete/addProcessLogsFromTextChunk
      // finalizeAssistantMessage 内部会 finalizeProcessLogs，手动调用可能导致双重 finalize
      const rawText = state.accumulatedText || '';
      const extracted = extractAllContentFromText(rawText);
      const cleanText = extracted.cleanText || '已完成回复生成';

      await finalizeAssistantMessage({
        id: createLocalId(),
        text: formatFinalAssistantText({
          text: cleanText,
          intentName: state.currentIntent?.name,
          userText: state.userText,
          hasVisualContext: state.currentAttachments.length > 0 || state.currentRefs.length > 0,
        }),
        type: 'text',
      });
    }

    // 🔵 v3-#6: 移除末尾重复的 resetStreamingState 调用
    // finalizeAssistantMessage 内部已通过原子操作（setState + STREAMING_RESET）完成重置
    const sid = useAgentStore.getState().sessionId;
    if (sid) {
      useAgentStore.getState().setSelectedHistoryId(sid);
    }
    state.refreshChatHistory();
  }, [finalizeAssistantMessage, ensureCanvasGenerationNode, syncCanvasGenerationNodeResult, updateCanvasNode]);

  return { handleComplete };
}
